import React from 'react';
import MemoIconREASolo from '../icons/IconREASolo';
import MemoIconREAPartner from '../icons/IconREAPartner';
import MemoIconREATeam from '../icons/IconREATeam';
import MemoIconREAFreemium from '../icons/IconREAFreemium';
import MemoIconCheckmarkCircle from '../icons/IconCheckmarkCircle';
import MemoIconLOStarter from '../icons/IconLOStarter';
import MemoIconLOPro from '../icons/IconLOPro';
import MemoIconLOUnlimited from '../icons/IconLOUnlimited';
import { getPlanDetails } from '../planDetails';
import {
  isRealEstatePlan,
  isInsurancePlan,
  isTitlePlan,
  LENDER_PLANS,
  OPEN_LENDER_PLANS
} from '../services/planService';

// The selected checkmark is absolutely positioned and scaled, so cards with long
// plan names must reserve room on the right or the title runs under it.
const CHECKMARK_CLEARANCE = 72;

/**
 * Plan option component for consistent rendering
 */
const PlanOption = ({
  planCode,
  isSelected,
  onClick,
  icon: Icon,
  title,
  subtitle,
  checkmarkColor = '#F51E84',
  checkmarkBgColor = '#fff',
  titleColor,
  disabled = false
}) => (
  <div
    className={`plan-option capacity-option ${planCode} ${isSelected ? 'selected' : ''}`}
    onClick={disabled ? undefined : onClick}
    tabIndex={disabled ? -1 : 0}
    role="button"
    style={{ position: 'relative', cursor: disabled ? 'default' : 'pointer' }}
  >
    <div className="flex gap-2" style={{ alignItems: 'center' }}>
      {Icon && <Icon width={48} height={48} />}
      <div>
        {titleColor ? (
          <p style={{ color: titleColor }}>{title}</p>
        ) : (
          <h3>{title}</h3>
        )}
        <p className="sub-text">{subtitle}</p>
      </div>
    </div>
    {isSelected && (
      <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
        <MemoIconCheckmarkCircle color={checkmarkColor} checkMarkColor={checkmarkBgColor} />
      </span>
    )}
  </div>
);

/**
 * Loading state component
 */
const LoadingPlanOption = () => (
  <div
    className="plan-option capacity-option"
    style={{
      position: 'relative',
      opacity: 0.6,
      cursor: 'default',
      minHeight: '100px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}
  >
    <div style={{ textAlign: 'center', color: '#aaa' }}>
      <div style={{
        display: 'inline-block',
        width: '24px',
        height: '24px',
        border: '3px solid rgba(245, 30, 132, 0.3)',
        borderTop: '3px solid #F51E84',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        marginBottom: '8px'
      }}></div>
      <p style={{ margin: 0, fontSize: '14px' }}>Loading plan information...</p>
    </div>
  </div>
);

/**
 * Real Estate Agent plan selection
 */
const RealEstatePlanSelection = ({ plan, sponsorId, freemiumAvailable, onPlanChange }) => (
  <fieldset className="form-section-single-fieldset">
    <div className="form-section-single-legend-container mt-8">
      <legend className="form-section-single-legend">Choose your plan</legend>
    </div>
    <div className="plan-selection vertical">
      {freemiumAvailable && (
        <PlanOption
          planCode="freemium-starter"
          isSelected={plan === 'freemium-starter'}
          onClick={() => onPlanChange('freemium-starter')}
          icon={MemoIconREAFreemium}
          title="Freemium"
          subtitle={`${getPlanDetails('freemium-starter').capacityAmount} ${getPlanDetails('freemium-starter').capacityLabel}`}
          checkmarkColor="#CE14BE"
          checkmarkBgColor="#F7F5FA"
        />
      )}
      {!sponsorId && (
        <PlanOption
          planCode="hb-agent-solo"
          isSelected={plan === 'hb-agent-solo'}
          onClick={() => onPlanChange('hb-agent-solo')}
          icon={MemoIconREASolo}
          title="Solo"
          subtitle={`${getPlanDetails('hb-agent-solo').capacityAmount} ${getPlanDetails('hb-agent-solo').capacityLabel}`}
          checkmarkColor="#EEFF41"
          checkmarkBgColor="#000"
        />
      )}
      <PlanOption
        planCode="hb-agent-partner"
        isSelected={plan === 'hb-agent-partner'}
        onClick={() => onPlanChange('hb-agent-partner')}
        icon={MemoIconREAPartner}
        title="Partner"
        subtitle={`${getPlanDetails('hb-agent-partner').capacityAmount} ${getPlanDetails('hb-agent-partner').capacityLabel}`}
        checkmarkColor="#F51E84"
        checkmarkBgColor="#fff"
      />
      {!sponsorId && (
        <PlanOption
          planCode="hb-rea-team"
          isSelected={plan === 'hb-agent-team'}
          onClick={() => onPlanChange('hb-agent-team')}
          icon={MemoIconREATeam}
          title="Team"
          subtitle={`${getPlanDetails('hb-agent-team').capacityAmount} ${getPlanDetails('hb-agent-team').capacityLabel}`}
          checkmarkColor="#499792"
          checkmarkBgColor="#fff"
        />
      )}
    </div>
  </fieldset>
);

/**
 * Lender plan selection
 */
const LenderPlanSelection = ({ plan, onPlanChange, formatCurrency }) => {
  const unlimitedDetails = getPlanDetails('hb-lender-unlimited');

  return (
    <fieldset className="form-section-single-fieldset">
      <div className="form-section-single-legend-container mt-8">
        <legend className="form-section-single-legend">Choose your capacity</legend>
      </div>
      <div className="plan-selection vertical" role="radiogroup" aria-label="Choose your capacity">
        <div
          className={`plan-option capacity-option hb-lender-starter ${plan === 'hb-lender-starter' ? 'selected' : ''}`}
          onClick={() => onPlanChange('hb-lender-starter')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlanChange('hb-lender-starter'); } }}
          tabIndex={0}
          role="radio"
          aria-checked={plan === 'hb-lender-starter'}
        >
          <MemoIconLOStarter />
          <div>
            <p style={{ color: "#EEFF41" }}>Starter</p>
            <h3>{getPlanDetails('hb-lender-starter').capacityAmount} <span className="option-title">{getPlanDetails('hb-lender-starter').capacityLabel}</span></h3>
            <p className="sub-text">{getPlanDetails('hb-lender-starter').secondaryCapacityAmount} {getPlanDetails('hb-lender-starter').secondaryCapacityLabel}</p>
          </div>
          {plan === 'hb-lender-starter' && (
            <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
              <MemoIconCheckmarkCircle color="#EEFF41" />
            </span>
          )}
        </div>
        <div
          className={`plan-option capacity-option hb-lender-pro ${plan === 'hb-lender-pro' ? 'selected' : ''}`}
          onClick={() => onPlanChange('hb-lender-pro')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlanChange('hb-lender-pro'); } }}
          tabIndex={0}
          role="radio"
          aria-checked={plan === 'hb-lender-pro'}
        >
          <MemoIconLOPro />
          <div>
            <p style={{ color: "#fe8cc2" }}>Pro</p>
            <h3>{getPlanDetails('hb-lender-pro').capacityAmount} <span className="option-title">{getPlanDetails('hb-lender-pro').capacityLabel}</span></h3>
            <p className="sub-text">{getPlanDetails('hb-lender-pro').secondaryCapacityAmount} {getPlanDetails('hb-lender-pro').secondaryCapacityLabel}</p>
          </div>
          {plan === 'hb-lender-pro' && (
            <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
              <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
            </span>
          )}
        </div>
        <div
          className={`plan-option capacity-option hb-lender-unlimited ${plan === 'hb-lender-unlimited' ? 'selected' : ''}`}
          onClick={() => onPlanChange('hb-lender-unlimited')}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPlanChange('hb-lender-unlimited'); } }}
          tabIndex={0}
          role="radio"
          aria-checked={plan === 'hb-lender-unlimited'}
        >
          <MemoIconLOUnlimited />
          <div>
            <p style={{ color: "#79B9FC" }}>Unlimited</p>
            <h3>{unlimitedDetails.capacityAmount} <span className="option-title">{unlimitedDetails.capacityLabel}</span></h3>
            <p className="sub-text">{unlimitedDetails.secondaryCapacityAmount} {unlimitedDetails.secondaryCapacityLabel}</p>
          </div>
          {plan === 'hb-lender-unlimited' && (
            <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', zIndex: 2 }}>
              <MemoIconCheckmarkCircle color="#79B9FC" />
            </span>
          )}
        </div>
      </div>
    </fieldset>
  );
};

/**
 * Single plan display for open partner flows (insurance, title, lender without companyId)
 */
const SinglePlanDisplay = ({ plan, planDetails, icon: Icon = MemoIconREAPartner }) => (
  <fieldset className="form-section-single-fieldset">
    <div className="form-section-single-legend-container mt-8">
      <legend className="form-section-single-legend">Your plan</legend>
    </div>
    <div className="plan-selection vertical">
      <div
        className={`plan-option capacity-option ${plan} selected`}
        tabIndex={0}
        role="button"
        style={{ position: 'relative', paddingRight: CHECKMARK_CLEARANCE }}
      >
        <div className="flex gap-2" style={{ alignItems: 'center' }}>
          <Icon width={48} height={48} style={{ flexShrink: 0 }} />
          <div>
            <h3>{planDetails?.planName}</h3>
            {planDetails?.features ? (
              <ul style={{
                listStyle: 'disc',
                margin: '0.5rem 0 0 1.2rem',
                padding: 0,
                fontSize: '1rem',
                color: '#fff',
                opacity: 0.88
              }}>
                {planDetails.features.map((feature, index) => (
                  <li key={`${plan}-${index}`} style={{ marginBottom: '0.25rem' }}>{feature}</li>
                ))}
              </ul>
            ) : (
              <p className="sub-text">{planDetails?.capacityAmount} {planDetails?.capacityLabel}</p>
            )}
          </div>
        </div>
        <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
          <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
        </span>
      </div>
    </div>
  </fieldset>
);

/**
 * Company plan selection
 */
const CompanyPlanSelection = ({ plan, companyPlans, onPlanChange }) => (
  <fieldset className="form-section-single-fieldset">
    <div className="form-section-single-legend-container mt-8">
      <legend className="form-section-single-legend">
        {companyPlans.length === 1 ? 'Your plan' : 'Choose your plan'}
      </legend>
    </div>
    <div className="plan-selection vertical">
      {companyPlans.map((companyPlan) => {
        const companyPlanDetails = getPlanDetails(companyPlan.attributes?.code || companyPlan.attributes?.['plan-code']);
        const isSelected = plan === companyPlanDetails?.code;

        return (
          <div
            key={companyPlan.id}
            className={`plan-option capacity-option ${isSelected ? 'selected' : ''}`}
            onClick={() => companyPlans.length > 1 && onPlanChange(companyPlanDetails?.code)}
            tabIndex={0}
            role="button"
            style={{ position: 'relative', paddingRight: CHECKMARK_CLEARANCE, cursor: companyPlans.length > 1 ? 'pointer' : 'default' }}
          >
            <div className="flex gap-2">
              <MemoIconLOPro style={{ flexShrink: 0 }} />
              <div>
                <h3>{companyPlanDetails?.planName}</h3>
                {companyPlanDetails?.features ? (
                  <ul style={{
                    listStyle: 'disc',
                    margin: '0.5rem 0 0 1.2rem',
                    padding: 0,
                    fontSize: '1rem',
                    color: '#fff',
                    opacity: 0.88
                  }}>
                    {companyPlanDetails.features.map((feature, index) => (
                      <li key={`${companyPlanDetails?.code}-${index}`} style={{ marginBottom: '0.25rem' }}>{feature}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="sub-text">{companyPlanDetails?.capacityAmount} {companyPlanDetails?.capacityLabel}</p>
                )}
              </div>
            </div>
            {isSelected && (
              <span style={{ position: 'absolute', right: 24, top: '50%', transform: 'translateY(-50%) scale(1.8)', borderRadius: '50%', padding: '4px', zIndex: 2 }}>
                <MemoIconCheckmarkCircle color="#F51E84" checkMarkColor="#fff" />
              </span>
            )}
          </div>
        );
      })}
    </div>
  </fieldset>
);

/**
 * Main PlanSelectionSection component
 */
const PlanSelectionSection = ({
  plan,
  sponsorId,
  companyId,
  freemiumAvailable,
  isLoadingCompanyData,
  companyPlans,
  onPlanChange,
  formatCurrency,
  selectNewPlanUrl
}) => {
  // Company-specific plans
  if (companyId) {
    if (isLoadingCompanyData) {
      return (
        <fieldset className="form-section-single-fieldset">
          <div className="form-section-single-legend-container mt-8">
            <legend className="form-section-single-legend">Loading your plan...</legend>
          </div>
          <div className="plan-selection vertical">
            <LoadingPlanOption />
          </div>
        </fieldset>
      );
    }

    if (companyPlans.length > 0) {
      return (
        <CompanyPlanSelection
          plan={plan}
          companyPlans={companyPlans}
          onPlanChange={onPlanChange}
        />
      );
    }
  }

  // Real Estate Agent plans
  if (isRealEstatePlan(plan)) {
    return (
      <RealEstatePlanSelection
        plan={plan}
        sponsorId={sponsorId}
        freemiumAvailable={freemiumAvailable}
        onPlanChange={onPlanChange}
      />
    );
  }

  // Lender plans
  if (LENDER_PLANS.includes(plan)) {
    return (
      <LenderPlanSelection
        plan={plan}
        onPlanChange={onPlanChange}
        formatCurrency={formatCurrency}
      />
    );
  }

  // Open single-plan partner flows (no companyId). Insurance/title (e.g. FNF) and
  // opted-in lender association offers (e.g. WAMP) land here via ?plan=<code>.
  if (isInsurancePlan(plan) || isTitlePlan(plan)) {
    return (
      <SinglePlanDisplay
        plan={plan}
        planDetails={getPlanDetails(plan)}
      />
    );
  }

  if (OPEN_LENDER_PLANS.includes(plan)) {
    return (
      <SinglePlanDisplay
        plan={plan}
        planDetails={getPlanDetails(plan)}
        icon={MemoIconLOPro}
      />
    );
  }

  return null;
};

export default PlanSelectionSection;
